<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="ontology-tree" :class="{selector: allowSelection, draggable: allowDrag, editable: allowEdition}">
  <sl-vue-tree v-model="treeNodes" :allowMultiselect="false" @select="select" @drop="drop" ref="tree">
    <template #toggle="{node}">
      <template v-if="!node.data.hidden && !node.isLeaf && node.children.length > 0">
        <i :class="['tree-toggle', 'fas', node.isExpanded ? 'fa-angle-down' : 'fa-angle-right']"></i>
      </template>
      <div class="sl-vue-tree-gap"></div>
    </template>

    <template #title="{node}">
      <button
        v-if="!node.data.hidden && node.data.isAddTerm"
        class="button is-small add-group-term"
        type="button"
        @click.stop="startTermCreation(node.data.ontologyModel)"
      >
        <span class="icon is-small add-action-icon"><i class="fas fa-plus-circle"></i></span>
        {{$t('add-term')}}
      </button>
      <div
        v-else-if="!node.data.hidden && node.data.isOntology"
        class="ontology-group-title"
      >
        <span class="ontology-group-name">{{ node.data.name }}</span>
        <span v-if="node.data.isAI" class="ontology-ai-badge">AI</span>
      </div>
      <div v-else-if="!node.data.hidden" class="tree-selector">
        <i class="tree-checkbox"
          v-if="allowSelection && node.data.selectable !== false"
          :class="classNames(node)">
        </i>
        <cytomine-term :term="node.data" />
      </div>
      <div v-else></div>
    </template>

    <template #sidebar="{node}">
      <slot v-if="!node.data.hidden" name="custom-sidebar" :term="node.data">
        <div v-if="allowEdition" class="buttons">
          <button class="button is-small" @click="startTermUpdate(node)">
            <span class="icon is-small">
              <i class="fas fa-edit"></i>
            </span>
          </button>
          <button class="button is-small" @click="confirmTermDeletion(node)">
            <span class="icon is-small">
              <i class="far fa-trash-alt"></i>
            </span>
          </button>
        </div>
      </slot>
    </template>
  </sl-vue-tree>

  <slot v-if="noResult" name="no-result">
    <em class="has-text-grey no-result">{{$t('no-result')}}</em>
  </slot>

  <div v-if="allowEdition || (allowNew && !groupByOntology)" class="add-term-container">
    <button class="button is-small" type="button" @click="startTermCreation()">
      <span class="icon is-small add-action-icon"><i class="fas fa-plus-circle"></i></span>
      {{$t('add-term')}}
    </button>
  </div>

  <div v-if="allowNewOntology" class="add-ontology-container">
    <button class="button is-small" type="button" data-testid="add-ontology" @click="$emit('addOntology')">
      <span class="icon is-small add-action-icon"><i class="fas fa-sitemap"></i></span>
      {{$t('add-term-tree')}}
    </button>
  </div>

</div>
</template>

<script>
import SlVueTree from 'sl-vue-tree';
import CytomineTerm from './CytomineTerm';
import TermModal from './TermModal';
import {Term} from '@/api';
import {getWildcardRegexp} from '@/utils/string-utils';

export default {
  name: 'ontology-tree',
  model: {
    prop: 'selectedNodes',
    event: 'setSelectedNodes'
  },
  props: {
    ontology: {type: Object},
    ontologies: {type: Array, default: null}, // 新增ontologies属性
    groupByOntology: {type: Boolean, default: false},
    additionalNodes: {type: Array, default: () => []},
    startWithAdditionalNodes: {type: Boolean, default: false},
    searchString: {type: String, default: ''},
    selectedNodes: {type: Array, default: () => []},
    allowSelection: {type: Boolean, default: true},
    multipleSelection: {type: Boolean, default: true},
    allowDrag: {type: Boolean, default: false},
    allowEdition: {type: Boolean, default: false},
    allowNew: {type: Boolean, default: false},
    allowNewOntology: {type: Boolean, default: false},
    hiddenNodes: {type: Array, default: () => []}
  },
  components: {
    SlVueTree,
    CytomineTerm
  },
  data() {
    return {
      treeNodes: [],
      internalSelectedNodes: [],
      editedNode: null,
      editedOntology: null
    };
  },
  computed: {
    regexp() {
      return getWildcardRegexp(this.searchString);
    },
    noResult() {
      return this.treeNodes.every(node => node.data.hidden);
    }
  },
  watch: {
    ontology() {
      this.makeTree();
    },
    ontologies() { // 监听ontologies的变化
      this.makeTree();
    },
    additionalNodes() {
      this.makeTree();
    },
    selectedNodes() {
      this.internalSelectedNodes = this.selectedNodes.slice();
      this.refreshNodeSelection();
    },
    regexp() {
      this.filter();
    },
    hiddenNodes() {
      this.filter();
    }
  },
  methods: {
    makeTree() {
      // 构建基本节点树
      let nodes = (this.ontology) ? this.createSubTree(Array.isArray(this.ontology.children.array)?this.ontology.children.array.slice():this.ontology.children) : [];
      
      // 添加additionalNodes
      let additionalNodes = this.createSubTree(this.additionalNodes.slice());

      // 根据startWithAdditionalNodes决定顺序
      let baseNodes = this.startWithAdditionalNodes ? additionalNodes.concat(nodes) : nodes.concat(additionalNodes);
      
      // 如果提供了ontologies且不为空，则添加到baseNodes之后
      if (this.ontologies && Array.isArray(this.ontologies) && this.ontologies.length > 0) {
        if (this.groupByOntology) {
          baseNodes = baseNodes.concat(
            this.ontologies
              .filter(ontology => ontology)
              .map(ontology => this.createOntologyNode(ontology))
          );
        } else {
          for (let ontology of this.ontologies) {
            if (ontology && Array.isArray(ontology.children)) {
              let ontologyNodes = this.createSubTree(ontology.children.slice(), ontology.id);
              baseNodes = baseNodes.concat(ontologyNodes);
            }
          }
        }
      }

      this.treeNodes = baseNodes;

      this.filter();
    },

    createSubTree(terms, ontologyId = this.ontology?.id || null) {
      return terms.map(term => this.createNode(term, ontologyId));
    },

    createOntologyNode(ontology) {
      const terms = Array.isArray(ontology.children?.array)
        ? ontology.children.array
        : (Array.isArray(ontology.children) ? ontology.children : []);

      return {
        title: ontology.name,
        isLeaf: false,
        isDraggable: false,
        isExpanded: true,
        isSelected: false,
        data: {
          id: `ontology-${ontology.id}`,
          name: ontology.name,
          ontology: ontology.id,
          hidden: false,
          isAI: /\s\(AI\)$/.test(ontology.name || ''),
          isOntology: true,
          selectable: false
        },
        children: [
          ...this.createSubTree(terms, ontology.id),
          ...(this.allowNew ? [this.createAddTermNode(ontology)] : [])
        ]
      };
    },

    createAddTermNode(ontology) {
      return {
        title: this.$t('add-term'),
        isLeaf: true,
        isDraggable: false,
        isExpanded: false,
        isSelected: false,
        data: {
          id: `add-term-${ontology.id}`,
          hidden: false,
          isAddTerm: true,
          isOntology: false,
          ontology: ontology.id,
          ontologyModel: ontology,
          selectable: false
        },
        children: []
      };
    },

    createNode(term, ontologyId = this.ontology?.id || null) {
      return {
        title: term.name,
        isLeaf: false, // all terms can be used as parent for drag and drop
        isDraggable: this.allowDrag,
        isExpanded: true,
        isSelected: this.internalSelectedNodes.includes(term.id),
        data: {
          id: term.id,
          name: term.name,
          color: term.color,
          parent: term.parent,
          ontology: ontologyId,
          hidden: false,
          isOntology: false,
          selectable: true
        },
        children: term.children && term.children.length > 0
          ? this.createSubTree(term.children, ontologyId)
          : []
      };
    },

    filter() {
      this.applyToAllNodes(node => {
        if (node.data.isAddTerm) {
          node.data.hidden = true;
          return;
        }
        let match = this.regexp.test(node.title) && !this.hiddenNodes.includes(node.data.id);
        if (node.children) {
          let matchInChildren = node.children.some(child => !child.data.hidden); // OK because applyToAllNodes performs bottom-up operations
          node.isExpanded = matchInChildren;
          match = match || matchInChildren;
        }
        node.data.hidden = !match;
      });
      this.treeNodes
        .filter(node => node.data.isOntology)
        .forEach(node => {
          const addTermNode = node.children.find(child => child.data.isAddTerm);
          if (addTermNode) {
            addTermNode.data.hidden = node.data.hidden;
          }
        });
    },

    classNames(node) {
      if (this.multipleSelection) {
        return node.isSelected ? ['fas', 'fa-check-square'] : ['far', 'fa-square'];
      } else {
        return node.isSelected ? ['fas', 'fa-dot-circle'] : ['far', 'fa-circle'];
      }
    },

    select(nodes, event) {
      if (!this.allowSelection) {
        return;
      }

      if (this.clickOnTreeSelector(event.target)) {
        const selectableNodes = nodes.filter(node => node.data.selectable !== false);
        selectableNodes.forEach(node => {
          if (this.multipleSelection) {
            let indexSelected = this.internalSelectedNodes.indexOf(node.data.id);
            if (indexSelected >= 0) {
              this.internalSelectedNodes.splice(indexSelected, 1);
              this.$emit('unselect', node.data.id);
            } else {
              this.internalSelectedNodes.push(node.data.id);
              this.$emit('select', node.data.id);
            }
          } else {
            if (this.internalSelectedNodes.includes(node.data.id)) {
              this.internalSelectedNodes = [];
            } else {
              this.internalSelectedNodes = [node.data.id];
            }

            this.$emit('select', node.data.id);
          }
        });
        if (selectableNodes.length) {
          this.$emit('setSelectedNodes', this.internalSelectedNodes);
        }
      }

      this.refreshNodeSelection();
    },
    refreshNodeSelection() {
      if (!this.allowSelection) {
        return;
      }

      this.applyToAllNodes(node => {
        node.isSelected = this.internalSelectedNodes.some(id => id === node.data.id);
      });
    },
    clickOnTreeSelector(elem) {
      if (elem.classList.contains('tree-selector')) {
        return true;
      }
      return elem.parentElement ? this.clickOnTreeSelector(elem.parentElement) : false;
    },
    applyToAllNodes(fct, nodes = this.treeNodes) {
      nodes.forEach(node => {
        if (node.children) {
          this.applyToAllNodes(fct, node.children);
        }
        fct(node);
      });
    },

    startTermCreation(ontology = this.ontology) {
      this.editedNode = null;
      this.editedOntology = ontology;
      this.openModal();
    },
    createTerm(term) {
      const ontology = this.editedOntology || this.ontology;
      const group = ontology && this.treeNodes.find(node => {
        return node.data.isOntology && node.data.ontology === ontology.id;
      });
      if (group) {
        const actionIndex = group.children.findIndex(node => node.data.isAddTerm);
        const insertAt = actionIndex < 0 ? group.children.length : actionIndex;
        group.children.splice(insertAt, 0, this.createNode(term, ontology.id));
      } else {
        this.treeNodes.push(this.createNode(term));
      }
      this.$emit('newTerm', term, ontology);
    },

    startTermUpdate(node) {
      this.editedNode = node;
      this.editedOntology = this.ontology;
      this.openModal();
    },
    updateTerm(term) {
      this.$refs.tree.updateNode(this.editedNode.path, {data: {...term}});
    },

    openModal() {
      this.$buefy.modal.open({
        parent: this,
        component: TermModal,
        props: {
          term: this.editedNode ? this.editedNode.data : null,
          ontology: this.editedOntology || this.ontology
        },
        events: {
          newTerm: this.createTerm,
          updateTerm: this.updateTerm
        },
        hasModalCard: true,
        canCancel: ['escape', 'outside']
      });
    },

    drop(nodes, position) {
      nodes.forEach(async node => {
        let idParent = (position.placement === 'inside') ? position.node.data.id : position.node.data.parent;
        if (node.data.parent !== idParent) {
          try {
            await new Term(node.data).changeParent(idParent);
            this.applyToAllNodes(tmp => {
              if (tmp.data.id === node.data.id) {
                tmp.data.parent = idParent;
              }
            });
          } catch (error) {
            console.log(error);
            this.$notify({type: 'error', text: this.$t('notif-error-ontology-tree-update')});
          }
        } else {
          this.$notify({type: 'warn', text: this.$t('notif-warn-ontology-tree-order-not-persisted')});
        }
      });
    },

    confirmTermDeletion(node) {
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-deletion'),
        message: this.$t('confirm-deletion-term', {name: node.data.name}),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: () => this.deleteTerm(node)
      });
    },
    async deleteTerm(node) {
      try {
        await Term.delete(node.data.id);
        this.$refs.tree.remove([node.path]);
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-term-deletion')});
      }
    }
},
created() {
  this.internalSelectedNodes = this.selectedNodes.slice();
  this.makeTree();
}
};
</script>

<style scoped>
.add-term-container,
.add-ontology-container {
  text-align: center;
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

.add-group-term {
  margin: 0.25em 0;
}

.add-action-icon {
  margin-right: 0.35em !important;
}
</style>


<style lang="scss">
@import '../../assets/styles/dark-variables';

.ontology-tree {
  padding: 0;
}

.ontology-tree .tree-checkbox {
  margin-right: 10px;
  color: $dark-text-disabled;
  font-size: 1rem;
}

.ontology-tree.selector .sl-vue-tree-node-item {
  color: $dark-text-primary;
}

.ontology-tree.selector .sl-vue-tree-node-item:hover {
  background: $dark-bg-hover;
}

.ontology-tree.selector .sl-vue-tree-selected > .sl-vue-tree-node-item {
  background-color: $dark-bg-primary;
  font-weight: inherit;
}

.ontology-tree.selector .sl-vue-tree-selected > .sl-vue-tree-node-item:hover {
  background-color: $dark-bg-hover;
}

.ontology-tree .sl-vue-tree-selected > .sl-vue-tree-node-item .tree-checkbox {
  color: $primary;
}

.ontology-tree .tree-checkbox.fas {
  color: $primary;
}

.ontology-tree.selector .sl-vue-tree-gap {
  width: 24px;
}

.ontology-tree.selector .tree-selector {
  cursor: pointer;
  flex-grow: 1;
}

.ontology-tree .tree-selector {
  min-width: 0; /* to allow correct handling of overflow-wrap */
  color: $dark-text-primary;
}

.ontology-tree .tree-selector:hover .tree-checkbox {
  color: $primary;
}

.ontology-tree .ontology-group-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
  color: $dark-text-primary;
  cursor: default;
  font-size: 0.82rem;
  font-weight: 700;
}

.ontology-tree .ontology-group-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ontology-tree .ontology-ai-badge {
  flex: 0 0 auto;
  padding: 0.08rem 0.34rem;
  border: 1px solid rgba($primary, 0.4);
  border-radius: 4px;
  background: rgba($primary, 0.14);
  color: $primary;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.35;
}

.ontology-tree .no-result {
  margin-left: 20px;
  line-height: 1.5;
  font-size: 0.9rem;
  color: $dark-text-disabled;
}

.ontology-tree .buttons, .ontology-tree .button {
  margin-bottom: 0 !important;
}

.ontology-tree.editable .sl-vue-tree-sidebar {
  width: 100px;
  padding-left: 20px;
  flex-shrink: 0;
}

.ontology-tree.editable .sl-vue-tree-sidebar {
  display: flex;
  align-items: top;
}

.sl-vue-tree-node-item {
  background-color: $dark-bg-primary;
  color: $dark-text-primary;
}

.sl-vue-tree-cursor-before:before,
.sl-vue-tree-cursor-after:after {
  background-color: $primary;
}

.sl-vue-tree-node-item:hover {
  background-color: $dark-bg-hover;
}
</style>
